import { Check } from 'lucide-react';

// Where you are in a batch of receipts, and a way to get to any other one.
//
// Twenty numbered circles is 560px of row, which a phone does not have. So the
// same five-or-twenty steps are drawn twice: as a segmented bar that divides
// whatever width there is, and — from `sm` up, where the room exists — as the
// numbered dots, which are easier to aim at and say which receipt is which.

type StepStatus = 'saved' | 'skipped' | 'todo';

interface BatchStepperProps {
  total: number;
  currentIndex: number;
  statuses: StepStatus[];
  onJump?: (index: number) => void;
  onCancelBatch: () => void;
}

/** Colour is the whole language of the compact bar, so it is shared. */
function stepColor(status: StepStatus, isCurrent: boolean): string {
  if (status === 'saved') return 'bg-green-500 text-white';
  if (status === 'skipped') return 'bg-orange-400 text-white';
  return isCurrent ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-500';
}

export function BatchStepper({
  total,
  currentIndex,
  statuses,
  onJump,
  onCancelBatch,
}: BatchStepperProps) {
  if (total <= 1) return null;

  const steps = Array.from({ length: total }, (_, i) => ({
    index: i,
    status: statuses[i] ?? 'todo',
    isCurrent: i === currentIndex,
  }));

  return (
    <div className="border-b bg-gray-50 px-4 py-2.5 sm:px-5 sm:py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="hidden gap-1 sm:flex">
            {steps.map(({ index, status, isCurrent }) => (
              <button
                key={index}
                type="button"
                onClick={() => onJump?.(index)}
                aria-label={`Go to receipt ${index + 1}`}
                title={`Go to receipt ${index + 1}`}
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-transform hover:scale-110 ${stepColor(status, isCurrent)}${
                  isCurrent ? ' ring-2 ring-indigo-500 ring-offset-1' : ''
                }`}
              >
                {status === 'saved' ? <Check className="h-3.5 w-3.5" /> : index + 1}
              </button>
            ))}
          </div>
          <span className="whitespace-nowrap text-sm text-gray-500">
            Receipt {currentIndex + 1} of {total}
          </span>
        </div>

        <button
          type="button"
          onClick={onCancelBatch}
          className="shrink-0 text-sm text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>

      {/* The compact rendering: segments divide the row, so twenty of them fit
          as readily as three. Each is padded top and bottom to a target a thumb
          can hit without the bar itself becoming tall. */}
      <div className="flex gap-0.5 sm:hidden">
        {steps.map(({ index, status, isCurrent }) => (
          <button
            key={index}
            type="button"
            onClick={() => onJump?.(index)}
            aria-label={`Go to receipt ${index + 1}`}
            className="flex min-w-0 flex-1 items-center py-2"
          >
            <span
              className={`h-1.5 w-full rounded-full ${stepColor(status, isCurrent)}${
                isCurrent ? ' ring-1 ring-indigo-500 ring-offset-1' : ''
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
